import React, { Component } from 'react';
import { Link, withRouter, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DragDropContext } from 'react-beautiful-dnd';
import i18n from 'i18next';
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { translate } from 'react-i18next';
import { arrayMove } from 'react-sortable-hoc';

import Icon from 'components/Icon';

import Template from './Template';
import PageList from './PageList';
import Pagination from './Pagination';
import Controls from './Controls';
import Share from './Share';

import DataService from 'lib/DataService';
import Loading from 'components/Loading';

import UserContext from 'contexts/User';
import EditorContext from 'contexts/Editor';

import layoutConfig, {
	defaultImageSlot,
	defaultTextSlot,
	defaultPictogramSlot,
} from '../layoutConfig';

const defaultSlots = {
	image: defaultImageSlot,
	text: defaultTextSlot,
	pictogram: defaultPictogramSlot,
};

const Api = new DataService();

const EditorPage = styled.div`
	padding: 40px 60px 0;
	height: calc(100vh - 60px);
	width: calc(100vw - ${props => props.controlsOpen ? '350px' : '0px'});
`;

const Saving = styled.div`
	position: absolute;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 100%;
	background-color: rgba(0,0,0,0.1);
	z-index: 100;
`;

const Navigation = styled(Link)`
	position: fixed;
	top: calc(50% - 40px);
	width: 30px;
	height: 80px;
	background-color: rgba(255,255,255,1);
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s ease-out;

	svg, path {
		transition: fill 0.3s ease-out;
	}

	&:hover {
		width: 40px;

		svg, path {
			fill: #4A90E2;
		}
	}
`;

const PrevPage = styled(Navigation)`
	right: 100%;
	border-radius: 20px 0 0 20px;
	box-shadow: -4px 0px 8px rgba(0,0,0,0.1), -6px 0px 12px -4px rgba(0,0,0,0.15) inset;
`;

const NextPage = styled(Navigation)`
	left: 100%;
	border-radius: 0 20px 20px 0;
	box-shadow: 4px 0px 8px rgba(0,0,0,0.1), 6px 0px 12px -4px rgba(0,0,0,0.15) inset;
`;

class Editor extends Component {
	constructor(props) {
		super(props);

		this.state = {
			unsavedChanges: {},
			itemActiveIndex: -1,
			saving: false,
			editorState: {},
		}
	}

	static getDerivedStateFromProps(props, state) {
		if (props.activeSlot === null) {
			return {
				unsavedChanges: {},
				itemActiveIndex: -1,
				saving: state.saving,
			}
		}

		return null;
	}

	updateEditorState = (slotIndex, newEditorState) => this.setState(state => {
		const {
			lng,
			brochure: { content: { pages: pagesData }},
			match: { params: { pageId }},
		} = this.props;

		const editorState = { ...state.editorState };
		const unsavedChanges = { ...state.unsavedChanges };

		// Brochure data
		const pageData = pagesData.find(page => page.id === pageId);
		const slotData = pageData.slot[slotIndex];
		const localValue = slotData.data.value[lng];

		// Current values
		const currentPlainValue = newEditorState.getCurrentContent().getPlainText().trim();
		let currentMarkupValue = draftToHtml(convertToRaw(newEditorState.getCurrentContent())).trim();

		// Should we compare the plain text or markup
		const localValueHasMarkup = localValue.indexOf('</') !== -1;
		// Compare without newlines
		const valueHasChanges = localValue.replace(/\n/g, '') !== (localValueHasMarkup ? currentMarkupValue : currentPlainValue).replace(/\n/g, '');

		if (typeof editorState[slotIndex] === 'undefined') {
			editorState[slotIndex] = {
				fi: '',
				en: '',
			};
		}

		if (slotIndex === this.props.activeSlot) {
			if (valueHasChanges) {
				// Add to unsaved changes when there are changes
				unsavedChanges[lng] = currentMarkupValue;
			}
			else if (lng in unsavedChanges) {
				// Delete from unsaved unsaved changes when there are no changes
				delete unsavedChanges[lng];
			}
		}

	  editorState[slotIndex][lng] = newEditorState;

		return {
			editorState,
			unsavedChanges,
		};
	});

	activeIndexHandler = itemActiveIndex => {
		if (itemActiveIndex !== this.state.itemActiveIndex) {
			const hasChanges = Object.keys(this.state.unsavedChanges).length > 0;
			const confirmText = itemActiveIndex === -1 ?
				'Tallentamattomat muutokset menetetään.\n\nHaluatko jatkaa?'
				: 'Tallentamattomat muutokset menetetään jos vaihdat muokattavaa kuvaa.\n\nHaluatko jatkaa?';

			if (!hasChanges || window.confirm(confirmText)) {
				this.setState({
					unsavedChanges: {},
					itemActiveIndex,
				});
			}
		}
	};

	resetChangesHandler = () => this.setState({
		unsavedChanges: {},
		itemActiveIndex: -1,
		saving: false,
		editorState: {},
	});

	changesHandler = (changes, persist) => this.setState(state => {
		const unsavedChanges = {
			...state.unsavedChanges,
			...changes,
		};

	  return { unsavedChanges };
	}, () => {
		if (persist) {
			this.saveHandler();
		}
	});

	addItems = items => {
		const { brochure, activeSlot, match: { params: { pageId }}} = this.props;
		const brochureId = brochure.id;
		const brochureContent = JSON.parse(JSON.stringify(brochure.content));
		const pageContent = brochureContent.pages.find(page => page.id === pageId)
		const slotData = pageContent.slot[activeSlot];
		const { type } = slotData;

		switch(type) {
		case 'image':
			slotData.data = items;
			slotData.data = items.filter((image, index) => {
				return slotData.data.findIndex(image2 => image2.file === image.file) === index;
			});
			break;
		case 'pictogram':
			slotData.data.pictograms = items;
			slotData.data.pictograms = items.filter((item, index) => {
				return slotData.data.pictograms.findIndex(item2 => item2.field === item.field) === index;
			});
			break;
		default:
			break;
		}

		this.setState({ saving: true });

		Api.updateContent({ brochureId, pageId, pageContent })
			.then(res => {
				this.resetChangesHandler();
				this.props.updatePageDataHandler({ pageId, pageContent });
			});
	}

	saveHandler = action => {
	  const { unsavedChanges, itemActiveIndex } = this.state;
	  const { brochure, activeSlot, match: { params: { pageId }}} = this.props;
	  const brochureId = brochure.id;
	  const brochureContent = JSON.parse(JSON.stringify(brochure.content));
	  const pageContent = brochureContent.pages.find(page => page.id === pageId);
	  const slotData = pageContent.slot[activeSlot];
	  const { type } = slotData;
	  const newState = {};

	  switch(type) {
		case 'image':
			slotData.data = slotData.data
				.map((image, imageIndex) => ({ ...image, ...(imageIndex === itemActiveIndex ? unsavedChanges : {}) })) // Update active image with unsaved changes
				.filter((image, imageIndex) => {
					let keep = true;

					if (action === 'remove' && imageIndex === itemActiveIndex) {
						keep = false;
						newState.itemActiveIndex = -1;
					}

					return keep;
				});
			break;
		case 'text':
			slotData.data = {
				value: {
					fi: 'fi' in unsavedChanges ? unsavedChanges.fi : slotData.data.value.fi,
					en: 'en' in unsavedChanges ? unsavedChanges.en : slotData.data.value.en,
				}
			};

			slotData.data.value.fi = slotData.data.value.fi.replace(/\n/g, '').replace(/"/g, '”');
			slotData.data.value.en = slotData.data.value.en.replace(/\n/g, '').replace(/"/g, '”');
			break;
		case 'pictogram':
			slotData.data.pictograms = slotData.data.pictograms
				.map((item, itemIndex) => { // Update active image with unsaved changes
					const changes = {
						...item
					}

					if (itemIndex === itemActiveIndex) {
						for (const key in unsavedChanges.i18n) {
							changes.i18n[key] = unsavedChanges.i18n[key];
						}
					}

					return changes;
				})
				.filter((item, itemIndex) => {
					let keep = true;

					if (action === 'remove' && itemIndex === itemActiveIndex) {
						keep = false;
						newState.itemActiveIndex = -1;
					}

					return keep;
				});

			if ('title' in unsavedChanges) {
				slotData.data.title = {
					...slotData.data.title,
					...unsavedChanges.title,
				}
			}
			break;
		default:
			slotData.data = unsavedChanges;
			break;
		}

		this.setState({
			saving: true,
			...newState
		});

		Api.updateContent({ brochureId, pageId, pageContent })
			.then(res => {
				this.props.updatePageDataHandler({ pageId, pageContent });
				this.resetChangesHandler();
			});
	}

	updateSettingsHandler = option => {
	  // if this.props.activeSlot === null update page settings else update slot options
	  const { brochure, activeSlot, match: { params: { pageId }}} = this.props;
	  const brochureId = brochure.id;
	  const brochureContent = JSON.parse(JSON.stringify(brochure.content));
	  const pageContent = brochureContent.pages.find(page => page.id === pageId);

	  this.setState({ saving: true });

		if (activeSlot === null) { // Page settings
			pageContent.options = {
				...pageContent.options,
				...option
			};

			// If we're linking the page to a specific space
			// Check if page content has a text component and add space details to it
			// <h1>Space name
			// <h4>Space type and area
			// <p>Space description
			if (option.space) {
				const { building } = this.props;
				const spaceSlug = option.space;
				const space = building.spaces.find(space => space.slug === spaceSlug);

				const spaceImages = space.images.map(imageId => {
					return building.images.find(i => i.file === imageId);
				}).filter(image => typeof image !== 'undefined');

				pageContent.slot = pageContent.slot.map(slot => {
					if (slot.type === 'image') {
						slot.data = spaceImages.map(image => {
							return {
								tag: false,
								imageLanguage: false,
								...image,
								id: Api.generateUuid(),
								position: [50, 50],
								scale: 1,
								size: 'contain',
							};
						});
					}
					if (slot.type === 'text') {
						const fiDescription = space.i18n.fi.description.indexOf('/>') === -1 ? `<p>${space.i18n.fi.description}</p>` : space.i18n.fi.description;
						const enDescription = space.i18n.en.description.indexOf('/>') === -1 ? `<p>${space.i18n.en.description}</p>` : space.i18n.en.description;
						const m2 = '<span style=\'text-transform: lowercase;\'>m²</span>';
						const enFloorNumber = String(space.floor).split('-').map(number => {

							// Floor number is not a digit
							const floorNumber = number ? parseInt(number) : false;
							if (!floorNumber || isNaN(floorNumber) || String(floorNumber) !== number) {
								return number;
							}

							// Add suffic to floor number
							let suffix = 'th';
							switch (floorNumber) {
							case 1:
								suffix = 'st';
								break;
							case 2:
								suffix = 'nd';
								break;
							case 3:
								suffix = 'rd';
								break;
							}
							return number + suffix;
						}).join('-');

						slot.data.value = {
							fi: `
								<h3>${building.address}, ${building.zip_code} ${building.city}</h3>
								<h4>${space.i18n.fi.type} / ${space.floor}. kerros / ${space.area} ${m2}</h4>
								<h4></h4>${fiDescription}`.replace(/[\t\n\r]/g, ''),
							en: `
								<h3>${building.address}, ${building.zip_code} ${building.city}</h3>
								<h4>${space.i18n.en.type} / ${enFloorNumber} floor / ${space.area} ${m2}</h4>
								<h4></h4>${enDescription}`.replace(/[\t\n\r]/g, ''),
						};
					}

					return slot;
				})
			}

			this.props.newPageHandler({ loading: false });
		}
		else { // Slot settings
			const slotData = pageContent.slot[activeSlot];

			slotData.options = {
				...slotData.options,
				...option
			};
		}

		Api.updateContent({ brochureId, pageId, pageContent })
			.then(res => {
				this.resetChangesHandler();
			});

		this.props.updatePageDataHandler({ pageId, pageContent });
	}

	updatePageSettingsHandler = option => {
		const { brochure, match: { params: { pageId }}} = this.props;
		const brochureId = brochure.id;
		const brochureContent = JSON.parse(JSON.stringify(brochure.content));
		const pageContent = brochureContent.pages.find(page => page.id === pageId);

		pageContent.options = {
			...pageContent.options,
			...option
		};

		this.setState({ saving: true });
		this.props.newPageHandler({ loading: false });

		Api.updateContent({ brochureId, pageId, pageContent })
			.then(res => {
				this.resetChangesHandler();
			});

		this.props.updatePageDataHandler({ pageId, pageContent });
	}

	addPageHandler = layout => {
		const { building, lng, brochure, match: { params: { pageId }}, config } = this.props;
		const brochureId = brochure.id;
		const slot = [ ...layoutConfig[layout].defaultSlot ].map(slot => {
			if (typeof slot === 'string') {
				const ret = {
					id: Api.generateUuid(),
				};
				let buildingName = '';

				if (building.i18n[lng].neighborhood !== '') {
					buildingName += ` / ${building.i18n[lng].neighborhood}`;
				}

				switch (slot) {
				case 'buildingAddressLargeMap':
					ret.type = 'text';
					ret.options = {
						layout: 'default',
						'background-color': 'rgb(255,255,255,0)',
					};
					ret.data = {
						value: {
							fi: `<h1>${building.address}</h1><h4>${building.zip_code} ${building.city}${buildingName}</h4>`,
							en: `<h1>${building.address}</h1><h4>${building.zip_code} ${building.city}${buildingName}</h4>`,
						}
					};
					break;
				case 'buildingAddress':
					ret.type = 'text';
					ret.options = {
						layout: 'centered',
						'background-color': 'rgb(255,255,255,0)',
					};
					ret.data = {
						value: {
							fi: `<h1>${building.address}</h1><h4>${building.i18n.fi.neighborhood} / ${building.city}</h4>`,
							en: `<h1>${building.address}</h1><h4>${building.i18n.en.neighborhood} / ${building.city}</h4>`,
						}
					};
					break;
				case 'buildingImage':
					ret.type = 'image';
					ret.options = {
						layout: 'default',
						'background-color': 'rgb(255,255,255,0)',
					};
					ret.data = [];

					if (building.image) {
						ret.data = [{
							id: Api.generateUuid(),
							file: building.image.file,
							position: [50, 50],
							scale: 1,
							size: 'cover',
							tag: building.image.tag,
							name: building.image.name,
							description: building.image.description,
							href: building.image.href,
							aspect: building.image.aspect,
							conversions: building.image.conversions,
						}];
					}
					break;
				default:
					break;
				}

				return ret;

			} else {
				const id = Api.generateUuid();

				slot.id = id;

				if (slot.type === 'responsibility') {
					slot.data.logos = building.energy_certificate ? building.energy_certificate.split(',') : [];
					slot.data.items = config.buildingResponsibilities.filter(responsibility => {
						return !!building.i18n[lng][`responsibility_${responsibility.id}`];
					}).map(responsibility => responsibility.id);
				}

				return { ...slot };
			}
		});

		const pageContent = {
			id: pageId,
			layout,
			options: {
				'background-color': 'white',
				logo: layout === 'building',
				buildingPattern: false,
				layout: 'default',
				space: null,
			},
			slot,
		};

		this.setState({ saving: true });
		this.props.newPageHandler({ loading: false });

		Api.updateContent({ brochureId, pageId, pageContent })
			.then(res => {
				this.resetChangesHandler();
			});

		this.props.updatePageDataHandler({ pageId, pageContent });
	}

	removePageHandler = () => {
		const { brochure, match: { params: { pageId }} } = this.props;
		const brochureId = brochure.id;

		this.setState({ saving: true });

		Api.removeContent({ brochureId, pageId })
			.then(res => {
				this.resetChangesHandler();
			});

		this.props.removePageDataHandler({ pageId });
	}

	onSortEndHandler = ({ oldIndex, newIndex }) => {
		console.log({ oldIndex, newIndex });

		if (oldIndex !== newIndex && newIndex !== null) {
			const { brochure, activeSlot, match: { params: { pageId }}} = this.props;
			const brochureId = brochure.id;
			const brochureContent = JSON.parse(JSON.stringify(brochure.content));
			const pageContent = brochureContent.pages.find(page => page.id === pageId);
			const originalPageContent = JSON.parse(JSON.stringify(brochureContent.pages.find(page => page.id === pageId)));
			const slotData = pageContent.slot[activeSlot];
			const { type } = slotData;

			switch(type) {
			case 'image':
				slotData.data = arrayMove(slotData.data, oldIndex, newIndex);
				break;
			case 'pictogram':
				slotData.data.pictograms = arrayMove(slotData.data.pictograms, oldIndex, newIndex);
				break;
			default:
				break;
			}

			Api.updateContent({ brochureId, pageId, pageContent })
				.then(res => {
					if (res.code !== 200) {
						this.props.updatePageDataHandler({ pageId, originalPageContent });
					}

					this.resetChangesHandler();
				});

			this.props.updatePageDataHandler({ pageId, pageContent });
		}
	}

	// Slot type drag end
	onDragEnd = ({ destination, draggableId }) => {
		if (!destination) {
			return;
		}

		if (destination.droppableId === 'controls') {
			return;
		}

		const slotData = draggableId in defaultSlots ? { ...defaultSlots[draggableId] } : false;

		if (!slotData) {
			return;
		}

		slotData.id = Api.generateUuid();

		const { brochureId, pageId } = this.props.match.params;
		const pageIndex = this.props.brochure.content.pages.findIndex(page => page.id === pageId);

		if (pageIndex === -1) {
			return;
		}

		const pageContent = { ...this.props.brochure.content.pages[pageIndex] };

		pageContent.slot = pageContent.slot.map(slot => {
			if (slot.id === destination.droppableId) {
				return slotData;
			}

			return slot;
		})

		console.log(`Change slot ${destination.droppableId} to ${draggableId} on page ${pageId}.`);
		console.log({ slotData });

		this.setState({ saving: true });

		Api.updateContent({ brochureId, pageId, pageContent })
			.then(res => {
				this.resetChangesHandler();
				this.setState({ saving: false });
				this.props.updatePageDataHandler({ pageId, pageContent });
			});
	}

	render() {
		const { brochure } = this.props;
		const { pages } = brochure.content;
		const { language } = i18n;

		let pageId = false;

		// If :pageId in parameters then use it
		if ('pageId' in this.props.match.params && typeof this.props.match.params.pageId !== 'undefined') {
			pageId = this.props.match.params.pageId;
		}

		const pageData = pages.find(page => page.id === pageId);
		const noPageIdButPages = !pageId && pages.length;
		const redirectToPage = noPageIdButPages;
		const firstPage = noPageIdButPages ? pages[0].id : false;
		const firstPageUrl = `/${language}/t/brochure/${brochure.id}/${firstPage}/`;
		const brochureUrl = `/${language}/t/brochure/${brochure.id}/`;
		const pageIndex = pages.findIndex(page => page.id === pageId);
		const prevIndex = Math.max(0, pageIndex - 1);
		const nextIndex = Math.min(pages.length - 1, pageIndex + 1);
		const prevId = pages[prevIndex] ? pages[prevIndex].id : false;
		const nextId = pages[nextIndex] ? pages[nextIndex].id : false;

		if (typeof pageData === 'undefined') {

			// Add new page
			if (typeof this.props.location.state !== 'undefined' && 'selectedLayout' in this.props.location.state) {
				this.addPageHandler(this.props.location.state.selectedLayout);

				return <Loading />;
			}

			if ('pageId' in this.props.match.params && typeof this.props.match.params.pageId !== 'undefined') {
				return <Redirect to={brochureUrl} />
			}
		}

		return (
			<EditorContext.Consumer>
				{({
					activeSlot,
					pageListOpen,
					controlsOpen,
					togglePageListHandler,
					toggleControlsHandler,
					activeSlotHandler,
					updatePageDataHandler,
					updateContentHandler,
					movePageHandler,
				}) => (
					<DragDropContext onDragEnd={this.onDragEnd}>
						{
							redirectToPage &&
							<Redirect to={firstPageUrl} />
						}
						<UserContext.Consumer>
							{ user => (
								<React.Fragment>
									{
										pageId &&
										<EditorPage controlsOpen={controlsOpen}>
											<Template
												user={user}
												editable={brochure.type === 'normal' || user.can.update.master()}
												showReadOnlyNotice={true}
												building={brochure.mappedBuilding}
												pictograms={this.props.pictograms}
												config={this.props.config}
												brochureId={brochure.id}
												pageId={pageId}
												activeSlot={activeSlot}
												data={pageData}
												unsavedChanges={this.state.unsavedChanges}
												itemActiveIndex={this.state.itemActiveIndex}
												controlsOpen={this.props.controlsOpen}
												editorState={this.state.editorState}
												resetChangesHandler={this.resetChangesHandler}
												activeSlotHandler={activeSlotHandler}
												toggleControlsHandler={toggleControlsHandler}
												changesHandler={this.changesHandler}
												updateEditorState={this.updateEditorState}
											>
												{
													prevId && prevId !== pageId &&
														<PrevPage
															to={`/${language}/t/brochure/${brochure.id}/${prevId}/`}
															onClick={() => this.props.activeSlotHandler(null)}
														>
															<Icon icon="cheveron-left" />
														</PrevPage>
												}
												{
													nextId && nextId !== pageId &&
														<NextPage
															to={`/${language}/t/brochure/${brochure.id}/${nextId}/`}
															onClick={() => this.props.activeSlotHandler(null)}
														>
															<Icon icon="cheveron-right" />
														</NextPage>
												}
											</Template>
										</EditorPage>
									}
									<PageList
										user={user}
										editable={brochure.type === 'normal' || user.can.update.master()}
										brochureType={brochure.type}
										pages={pages}
										brochureId={brochure.id}
										orientation={brochure.orientation}
										open={pageListOpen}
										activePage={pageId}
										addPageHandler={this.addPageHandler}
										togglePageListHandler={togglePageListHandler}
										updateContentHandler={updateContentHandler}
										movePageHandler={movePageHandler}

										building={brochure.mappedBuilding}
										pictograms={this.props.pictograms}
										config={this.props.config}
										unsavedChanges={this.state.unsavedChanges}
										editorState={this.state.editorState}
									/>
									<Pagination
										pages={pages}
										brochureId={brochure.id}
										activePage={pageId}
										controlsOpen={controlsOpen}
										pageListOpen={pageListOpen}
										activeSlotHandler={activeSlotHandler}
									/>
								</React.Fragment>
							)}
						</UserContext.Consumer>
						{
							pageId &&
							<Controls
								building={brochure.mappedBuilding}
								pictograms={this.props.pictograms}
								brochureId={brochure.id}
								pageId={pageId}
								activeSlot={activeSlot}
								pageData={pageData}
								config={this.props.config}
								unsavedChanges={this.state.unsavedChanges}
								itemActiveIndex={this.state.itemActiveIndex}
								open={controlsOpen}
								saving={this.state.saving}
								editorState={this.state.editorState}
								changesHandler={this.changesHandler}
								resetChangesHandler={this.resetChangesHandler}
								saveHandler={this.saveHandler}
								onSortEndHandler={this.onSortEndHandler}
								addItems={this.addItems}
								activeIndexHandler={this.activeIndexHandler}
								toggleControlsHandler={toggleControlsHandler}
								updatePageDataHandler={updatePageDataHandler}
								removePageDataHandler={this.props.removePageDataHandler}
								updateSettingsHandler={this.updateSettingsHandler}
								updateEditorState={this.updateEditorState}
							/>
						}
						{
							this.state.saving &&
							<Saving />
						}
						{
							this.props.generatePDFOpen &&
							<Share
								brochure={brochure}
								baseUrl={this.props.config.baseUrl}
								toggleGeneratePdfOpen={this.props.toggleGeneratePdfOpen}
							/>
						}
					</DragDropContext>
				)}
			</EditorContext.Consumer>
		)
	}
}

Editor.propTypes = {
	t: PropTypes.func,
	match: PropTypes.object,
	location: PropTypes.object,
	lng: PropTypes.string,
	building: PropTypes.object,
	brochure: PropTypes.object,
	pictograms: PropTypes.array,
	activeSlot: PropTypes.number,
	pageListOpen: PropTypes.bool,
	controlsOpen: PropTypes.bool,
	generatePDFOpen: PropTypes.bool,
	config: PropTypes.object,
	togglePageListHandler: PropTypes.func,
	toggleControlsHandler: PropTypes.func,
	toggleGeneratePdfOpen: PropTypes.func,
	activeSlotHandler: PropTypes.func,
	updatePageDataHandler: PropTypes.func,
	removePageDataHandler: PropTypes.func,
	newPageHandler: PropTypes.func,
}

export default withRouter(translate('translations')(Editor));
